
import { Study } from "./study.model";
import { RunSampleLot } from "./run.model";
import { removeDuplicateCalLots, resolveCalLotForRegion, resolveCalLotFromSampleTypes } from "../utils/helper";
import { CalAdjustmentMode, isCalAdjustFinal, isCalibratorAdjustmentRunType, toApiWorkflow } from "../utils/run-type.util";

export type LotVersionRequest = {
  lotNumber: string;
  version?: number;
};

/**
 * Represents a calculation request containing information about a study, panel, and label.
 * It includes patient CSV files, control lot numbers, calibration lot number, sample information, and materials information.
 */
export class CalculationRequest {
  /**
   * @param finalValueAssignment `CalculateFinal` only: the per-report-item values to finalize.
   */
  constructor(study: Study, panelName: string, label: string, finalValueAssignment?: number[][]) {
    this.patient_csv = study.patientFileMetadata?.length ? study.patientFileMetadata.map(file => file.fileName) : [];
    this.patient_instrument_ids = study.patientFileMetadata?.length ? study.patientFileMetadata.map(file => file.instrumentId) : [];
    this.calibration_csv = study.calFileMetadata?.length ? study.calFileMetadata.map(file => file.fileName) : [];
    this.calibration_instrument_ids = study.calFileMetadata?.length ? study.calFileMetadata.map(file => file.instrumentId) : [];
    this.control_csv = study.controlFileMetadata?.length ? study.controlFileMetadata.map(file => file.fileName) : [];
    this.control_instrument_ids = study.controlFileMetadata?.length ? study.controlFileMetadata.map(file => file.instrumentId) : [];
    this.cntrlLotNums = study.qcLots?.length
      ? study.qcLots.map(lot => this.toLotVersionRequest(lot.masterLotNumber, lot.valueAssignmentVersion))
      : [];
    this.calLotNums = study.calLots?.length
      ? removeDuplicateCalLots(study.calLots).map(lot => this.toLotVersionRequest(lot.calLotNumber, lot.valueAssignmentVersion))
      : [];
    this.panelName = panelName;
    this.revisionNum= study.panelSpecs ? study.panelSpecs.revisionNumber : '';
    this.workflow = this.normalizeWorkflow(study.runType);
    this.testSpec = this.normalizeTestSpec(study.testSpec);
    this.calculate_averages = !!study.calculate_averages;
    this.formulation_stage = study.assignSpec || null;
    this.use_legacy_4pl = !!study.use_legacy_4pl;
    this.skipMissingSamples = !!study.skip_missing_samples;
    // The backend rejects the field on any other workflow.
    if (isCalibratorAdjustmentRunType(study.runType)) {
      // A Final study's CSV reruns its Final run, so it goes out in Final mode too.
      const isFinalRun = label === 'CalculateFinal' || (label === 'GetCsv' && isCalAdjustFinal(study));
      this.cal_Adjustment_Mode = isFinalRun ? 'Final' : 'Adjust';
    }

    this.sample_info = new SampleInfo(study);
    this.materials_info = new MaterialsInfo(study, label, finalValueAssignment);

  }

  patient_csv!: string[];
  patient_instrument_ids!: string[];
  calibration_csv!: string[];
  calibration_instrument_ids!: string[];
  control_csv!: string[];
  control_instrument_ids!: string[];
  calLotNums!: LotVersionRequest[];
  cntrlLotNums!: LotVersionRequest[];
  sample_info!: SampleInfo;
  materials_info!: MaterialsInfo;
  panelName!: string;
  revisionNum!: string;
  workflow!: string;
  testSpec!: string;
  calculate_averages!: boolean;
  formulation_stage: string | null;
  use_legacy_4pl!: boolean;
  skipMissingSamples!: boolean;
  cal_Adjustment_Mode?: CalAdjustmentMode;

  private normalizeWorkflow(runType: string | undefined): string {
    return toApiWorkflow(runType);
  }

  private normalizeTestSpec(testSpec: string | undefined): string {
    if (!testSpec) {
      return '';
    }
    if (testSpec.trim().toLowerCase() === 'adjustment') {
      return 'CalibratorAdjustment';
    }
    if (testSpec.trim().toLowerCase() === 'matched kit') {
      return 'MatchKit';
    }
    return testSpec;
  }

  private toLotVersionRequest(lotNumber: string | undefined, version: number | undefined): LotVersionRequest {
    const normalizedLotNumber = String(lotNumber ?? '').trim();
    const normalizedVersion = typeof version === 'number' && Number.isFinite(version)
      ? version
      : undefined;

    return normalizedVersion === undefined
      ? { lotNumber: normalizedLotNumber }
      : { lotNumber: normalizedLotNumber, version: normalizedVersion };
  }
}
/**
 * Represents sample-related information used in calculations.
 * It contains details like accession numbers, sample IDs, sample types, and required replicates.
 */
export class SampleInfo {
  constructor(study: Study) {
    const runSamples = study.runSamples;
   //  const runSampleLots = study.runSampleLots;

    if (runSamples && runSamples.length) { // && runSampleLots && runSampleLots.length) {
      this.accession_number = runSamples.map(sample => sample.accessionId);
      this.sample_id = runSamples.map(sample => sample.sampleId);
      this.sample_type = runSamples.map(sample => sample.sampleType);
      this.num_replicates = runSamples.map(sample => sample.reqNumReplicates);
    } else {
      this.accession_number = [];
      this.sample_id = [];
      this.sample_type = [];
      this.num_replicates = [];
    }
  }

  accession_number!: string[];
  sample_id!: (string | null)[]
  sample_type!: string[];
  num_replicates!: number[];
}

//export class KitLot {
//  runId!: string;
//  kitLotNumber!: string;
//  sampleType!: string[];
//}
/**
 * Represents materials-related information used in calculations.
 * This includes kit lots, assay names, reference lots, and value assignments.
 */
export class MaterialsInfo {
  constructor(study: Study, label: string, finalValueAssignment?: number[][]) {
    const reportContentItems = study?.reportContentItems?.reportContentItems;
    this.kit_Lot = [];
    this.reference_Lot = [];
    reportContentItems?.forEach(() => { this.reference_Lot.push(study?.refKitLot) });
    this.assay_Name = [];
    this.region = [];
    this.value_Assignment = [];
    this.original_Value_Assignment = [];
    this.run_Value_Optimization = [];
    const isCalibratorAdjustment =
      (study?.runType ?? '').replace(/\s+/g, '').toLowerCase() === 'calibratoradjustment';
    const isFinalCsv = label === 'GetCsv' && isCalAdjustFinal(study);
    this.calculation_Type = [];
    this.instrument_Id = [];
    this.manual_Filtering = [];
    this.cal_Lot = [];
    this.sample_Type_Overrides = [];
    const runSampleLots = study?.runSampleLots ?? [];
    const defaultSampleTypes = study?.runSamples?.map(sample => sample.sampleType || sample.defaultSampleType || '') ?? [];
    const runSampleLotsById = new Map<string, RunSampleLot>();
    // Keep a quick lookup of overrides so each report content row can grab its patient-level edits by ID.

    runSampleLots.forEach(runSampleLot => {
      const lotId = runSampleLot?.reportContentItemId;
      if (lotId !== undefined && lotId !== null) {
        runSampleLotsById.set(String(lotId), runSampleLot);
      }
    });

    if (reportContentItems && reportContentItems.length) {
      reportContentItems.forEach((rci, index) => {
        const itemRunSampleLot = rci?.reportContentItemId
          ? runSampleLotsById.get(String(rci.reportContentItemId))
          : undefined;
        // The Cal - Cal sample types in use name the cal lot; fall back to the region lookup when none do.
        const matchedCalLot =
          resolveCalLotFromSampleTypes(study?.calLots, rci.region, (itemRunSampleLot ?? runSampleLots[index])?.runSamples) ??
          resolveCalLotForRegion(study?.calLots, rci.region, study?.runSamples);
        this.cal_Lot.push(matchedCalLot?.calLotNumber ?? '');
        const manualFilteringSource = (rci as unknown as { manualFiltering?: unknown })?.manualFiltering;
        this.manual_Filtering.push(this.parseManualFiltering(manualFilteringSource));
        this.kit_Lot.push(rci.lot);
        this.assay_Name.push(rci.assay);
        this.region.push(rci.region.toString());
        this.calculation_Type.push(rci.resultType || 'Calculated');
        this.instrument_Id.push(rci.runId !== undefined && rci.runId !== null ? String(rci.runId) : '');
        const runSampleLot = rci?.reportContentItemId
          ? runSampleLotsById.get(String(rci.reportContentItemId))
          : undefined;
        this.sample_Type_Overrides.push(
          this.getSampleTypeOverrides(runSampleLot ?? runSampleLots[index], defaultSampleTypes)
        );
        if (label === 'CalculateWithOutAdjustment') {
          this.value_Assignment.push([]);
          this.original_Value_Assignment.push([]);
        }
        else if (label === 'CalculateWithAdjustment') {
          if (study?.adjustedCalLots?.valueAssignment?.length) {
            this.value_Assignment = study.adjustedCalLots.valueAssignment;
          } else {
            this.value_Assignment.push([]);
          }
          if (isCalibratorAdjustment && study?.adjustedCalLots?.originalValueAssignment?.length) {
            this.original_Value_Assignment = study.adjustedCalLots.originalValueAssignment;
          } else {
            this.original_Value_Assignment.push([]);
          }
        }
        else if (label === 'CalculateFinal') {
          // Final runs once on the finalized values: no baseline and no optimization.
          this.value_Assignment.push(finalValueAssignment?.[index] ?? []);
          this.original_Value_Assignment.push([]);
        }
        else if (isFinalCsv) {
          // The stored Final values, with no baseline, exactly as the Final run was requested.
          this.value_Assignment.push(study.adjustedCalLots?.valueAssignment?.[index] ?? []);
          this.original_Value_Assignment.push([]);
        }
        else if (label === 'GetCsv') {
          if (study?.calculationAdjustedResponse && study?.calculationAdjustedResponse.valueAssignment === 'AdjustedCalculate') {
            this.value_Assignment = study.adjustedCalLots.valueAssignment;
          } else {
            this.value_Assignment.push([]);
          }
          if (isCalibratorAdjustment && study?.calculationAdjustedResponse?.valueAssignment === 'AdjustedCalculate' && study?.adjustedCalLots?.originalValueAssignment?.length) {
            this.original_Value_Assignment = study.adjustedCalLots.originalValueAssignment;
          } else {
            this.original_Value_Assignment.push([]);
          }
        }
        const runValueOptimization = label === 'CalculateFinal' || isFinalCsv
          ? 'N/A'
          : this.normalizeOptimizationOption(
            (rci as unknown as { runValueOptimization?: string | boolean }).runValueOptimization
          );
        this.run_Value_Optimization.push(runValueOptimization);
      });
    }
  }

  kit_Lot!: string[];
  assay_Name!: string[];
  region!: string[];
  value_Assignment!: number[][];
  original_Value_Assignment!: number[][];
  reference_Lot!: string[];
  run_Value_Optimization!: string[];
  calculation_Type!: string[];
  instrument_Id!: string[];
  cal_Lot!: string[];
  manual_Filtering!: object[];
  sample_Type_Overrides!: string[][];

  private normalizeOptimizationOption(value: string | boolean | undefined): string {
    if (typeof value !== 'string') {
      return 'N/A';
    }

    const normalized = value.trim().toLowerCase().replace(/\s+/g, '_');
    if (normalized === 'n/a' || normalized === 'na') {
      return 'N/A';
    }
    if (normalized === 'powell') {
      return 'powell';
    }
    if (normalized === 'proportional_sweep') {
      return 'proportional_sweep';
    }

    return 'N/A';
  }

  private parseManualFiltering(source: unknown): Record<string, number[]> {
    if (!source) {
      return {};
    }
    if (typeof source === 'string') {
      try {
        const parsed = JSON.parse(source);
        if (parsed && typeof parsed === 'object') {
          return this.coerceManualFiltering(parsed);
        }
      } catch {
        return {};
      }
    }
    if (typeof source === 'object') {
      return this.coerceManualFiltering(source);
    }
    return {};
  }

  private coerceManualFiltering(source: unknown): Record<string, number[]> {
    if (!source || typeof source !== 'object') {
      return {};
    }
    const next: Record<string, number[]> = {};
    Object.entries(source as Record<string, unknown>).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        const reps = value
          .map(item => Number(item))
          .filter(item => Number.isFinite(item));
        next[key] = reps;
      }
    });
    return next;
  }

  private getSampleTypeOverrides(runSampleLot: RunSampleLot | undefined, defaultSampleTypes: string[]): string[] {
    if (!runSampleLot?.runSamples?.length) {
      return [];
    }

    // The calculation service only needs the selected type strings, so strip the rest of the form shape.
    const overrides = runSampleLot.runSamples.map(runSample => runSample.sampleType ?? '');
    const hasDifferences = overrides.some((sampleType, index) => {
      const defaultType = defaultSampleTypes[index] ?? '';
      return sampleType !== defaultType;
    });

    return hasDifferences ? overrides : [];
  }
}
/**
 * Represents the sample calculation results.
 * Contains calculated values, replicate counts, test results, and various statistics related to the sample.
 */
export class SampleCalcs {
  isDiscarded!: boolean;
  /** Display text for the Discard Reason column, as of the last calculation. */
  discardReason?: string;
  /** Discarded by the backend (not the operator), so the operator cannot undo it. */
  autoDiscarded?: boolean;
  /** Excluded from the mean as an outlier; shown like a discard but not one, and not toggleable. */
  isOutlier?: boolean;
  /** Set only in Failed Samples mode, where rows from several report content items share one table. */
  reportContentItemId?: string;
  assay?: string;
  kitLot?: string;
  instrument?: string;
  sampleId?: string;
  accessionId?: string;
  resultStatus?: string;
  repNum!: number;
  updatedRepNum!: number;
  beadCV!: number;
  beadCount!: number;
  countTime!: number;
  rfi!: number;
  normalizingBeadSignal!: number;
  calcFR!: number;
  reqNumReplicates!: number;
  meanRFI!: number;
  meanNormalizingBeadSignal!: number;
  meanFR!: number;
  instrumentConc!: number;
  calculatedConc!: number;
  finalValue!: number;
  aveValue!: number;
  finalRepCount!: number;
  repCountOk!: string;
  repSD!: number;
  repCV!: number;
  resultRepOK!: string;
  formattedTestResult!: number;
  testStatus!: string;
  testScore!: string;
  difference!: number;
  percentDifference!: number | string;
  ratio!: number | string;
  averageBeadCV!: number;
  averageBeadCount!: number;
  averageCountTime!: number;
}
